import { useCallback, useState } from "react";

import { TestPhase } from "../models/audit/controlSession";
import { useCreateControlSession } from "./useControlsV2";
import { useFyCycleField } from "./useFyCycleField";

/** Shared create-test dialog state for ControlsPageV2 and ControlSessionsSection.
 * Linked (Optro) submits send explicit name/phase defaults — the dialog hides those
 * inputs, so leftover state from a cancelled unlinked dialog must not ride along. */
export function useCreateSessionForm({ onCreated }: { onCreated: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionName, setSessionName] = useState("New Test");
  const [testPhase, setTestPhase] = useState<TestPhase>("not_defined");
  const { createSession, loading } = useCreateControlSession();
  const {
    fyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    initWithDefault,
    handleChange: handleFyCycleChange,
    confirmSetDefault,
    dismissSetDefault,
    resolveFyCycleId,
    reset: resetFyCycle,
  } = useFyCycleField();

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      setIsOpen(open);
      if (open) {
        await initWithDefault();
      }
    },
    [initWithDefault],
  );

  const handleCreateSession = useCallback(
    async (controlId: string, optroTestId: number | null) => {
      // A non-null id means this create maps to an Optro test: the dialog hid
      // the name/phase inputs, so submit with defaults, never leftover state
      // from an earlier unlinked dialog.
      const isOptroMapped = optroTestId !== null;
      try {
        const fyCycleId = await resolveFyCycleId();
        const sessionId = await createSession(
          controlId,
          isOptroMapped ? "New Test" : sessionName,
          isOptroMapped ? "not_defined" : testPhase,
          fyCycleId,
          optroTestId,
        );
        if (sessionId) {
          setIsOpen(false);
          setSessionName("New Test");
          setTestPhase("not_defined");
          resetFyCycle();
          onCreated();
        }
      } catch (error) {
        console.error("Failed to create session:", error);
      }
    },
    [
      sessionName,
      testPhase,
      resolveFyCycleId,
      createSession,
      resetFyCycle,
      onCreated,
    ],
  );

  return {
    isOpen,
    handleOpenChange,
    sessionName,
    setSessionName,
    testPhase,
    setTestPhase,
    fyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    handleFyCycleChange,
    confirmSetDefault,
    dismissSetDefault,
    loading,
    handleCreateSession,
  };
}
